import { useSiteContext } from '@zooplus/commons-lang';

/**
 * Formats a date according to the specified locale and formatting options.
 *
 * @param locale - The locale string (e.g. 'es-ES') used for formatting the date.
 * @param date - The date to format. Can be a string, number, or Date object.
 * @param options - Optional. An object specifying formatting options for the date (defaults to year, month, and day in 2-digit format).
 * @returns The formatted date string according to the locale and options.
 */
export function formatDate(
  locale: string,
  date: string | number | Date,
  options: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' },
) {
  return new Date(date).toLocaleDateString(locale, options);
}

/**
 * This hook returns the date formatting function bound to the current locale from `SiteContext`.
 * @returns A function that formats a date according to the current locale from `SiteContext`.
 */
export function useDateFormatter() {
  const { locale } = useSiteContext();

  return formatDate.bind(null, locale);
}

/**
 * Checks if a given date falls within the current quarter of the year.
 *
 * @param date - The date to check. Can be a Date object, a string, or a number (timestamp).
 * @returns True if the date is in the current quarter, false otherwise.
 */
export function isInThisQuarter(date: Date | string | number): boolean {
  const now = new Date();
  const targetDate = new Date(date);

  const currentQuarter = Math.floor(now.getMonth() / 3);
  const dateQuarter = Math.floor(targetDate.getMonth() / 3);

  return now.getFullYear() === targetDate.getFullYear() && currentQuarter === dateQuarter;
}

/**
 * Returns the end date of the current quarter.
 * Useful for testing purposes (in tests or storybook stories).
 * @returns A Date object representing the last day of the current quarter.
 */
export function getEndOfCurrentQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const endMonth = (quarter + 1) * 3 - 1;
  const endDate = new Date(now.getFullYear(), endMonth + 1, 0); // 0th day of next month = last day of this month
  return endDate;
}

/**
 * Returns a date that falls outside the current quarter.
 * Useful for testing purposes (in tests or storybook stories).
 * @returns A Date object representing a date outside the current quarter.
 */
export function getDateOutsideCurrentQuarter() {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  // Move to next quarter
  let nextQuarter = quarter + 1;
  let year = now.getFullYear();
  if (nextQuarter > 3) {
    nextQuarter = 0;
    year += 1;
  }
  const startMonth = nextQuarter * 3;
  // Pick the 15th day of the first month of the next quarter
  const date = new Date(year, startMonth, 15);
  return date;
}
